import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Badge from '../../components/ui/Badge.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { Field, Input } from '../../components/ui/Field.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import api from '../../lib/api.js'

/*
 * Company user management (admin) — inventory §5 Users tab. List members, assign
 * an existing user by email, remove a member. Owner and self can't be removed.
 */
export default function UsersTab() {
  const { t } = useTranslation()
  const { currentCompanyId, isAdmin } = useCompany()
  const { user } = useAuth()
  const { confirm, toast, notify } = useFeedback()
  const [users, setUsers] = useState([])
  const [allRoles, setAllRoles] = useState([])
  const [assigning, setAssigning] = useState(false)
  const [addingFor, setAddingFor] = useState(null) // user id showing the role picker

  const load = useCallback(() => {
    if (!currentCompanyId) return
    api.get(`/companies/${currentCompanyId}/users`).then(({ data }) => setUsers(data?.users || []))
  }, [currentCompanyId])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    api.get('/roles/').then(({ data }) => setAllRoles((data?.roles || []).map((r) => r.name || r).filter((n) => n !== 'super-administrateur'))).catch(() => {})
  }, [])

  // Optimistic role edit, persisted with PUT /companies/:id/users/:uid/roles.
  const setUserRoles = (userId, fn) => setUsers((us) => us.map((u) => (u.id === userId ? { ...u, roles: fn(u.roles || []) } : u)))
  const updateRoles = async (u, nextRoles) => {
    const prev = u.roles || []
    setUserRoles(u.id, () => nextRoles)
    try { await api.put(`/companies/${currentCompanyId}/users/${u.id}/roles`, { roles: nextRoles }); toast(t('settings.users.rolesUpdated'), 'success') }
    catch (e) { setUserRoles(u.id, () => prev); notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) }
  }
  const addRole = (u, role) => { setAddingFor(null); if (!(u.roles || []).includes(role)) updateRoles(u, [...(u.roles || []), role]) }
  const removeRole = (u, role) => updateRoles(u, (u.roles || []).filter((r) => r !== role))

  if (!isAdmin()) {
    return <Card style={{ textAlign: 'center', padding: 36 }}>
      <div style={{ color: 'var(--color-text-muted)', display: 'flex', justifyContent: 'center', marginBottom: 10 }}><Icon name="shield" size={32} strokeWidth={1.5} /></div>
      <div style={{ color: 'var(--color-text)' }}>{t('settings.users.noAdmin')}</div>
    </Card>
  }

  const remove = async (u) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('settings.users.confirmRemove'), message: u.email, confirmLabel: t('settings.users.remove'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/companies/${currentCompanyId}/users/${u.id}/unassign`); toast(t('settings.users.removed'), 'success'); load() }
    catch { toast(t('settings.errorGeneric'), 'error') }
  }

  return (
    <Card padding={0}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--color-border-row)' }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16 }}>{t('settings.users.title')}</h3>
        <Button variant="primary" icon="plus" onClick={() => setAssigning(true)}>{t('settings.users.assign')}</Button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: 'var(--color-bg-lilac)' }}>
          <th style={uth}>{t('settings.users.name')}</th><th style={uth}>{t('settings.users.email')}</th><th style={uth}>{t('settings.users.roles')}</th><th style={{ ...uth, textAlign: 'right' }}>{t('common.actions')}</th>
        </tr></thead>
        <tbody>
          {users.map((u) => {
            const self = u.id === user?.id
            return (
              <tr key={u.id} style={{ borderTop: '1px solid var(--color-border-row)' }}>
                <td style={utd}>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}{u.is_owner && <span style={{ marginLeft: 8 }}><Badge tone="info">{t('settings.users.owner')}</Badge></span>}</td>
                <td style={utd}>{u.email}</td>
                <td style={utd}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(u.roles || []).length === 0 && addingFor !== u.id && <span style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{t('settings.users.noRole')}</span>}
                    {(u.roles || []).map((r) => (
                      <span key={r} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 6px 3px 10px', borderRadius: 999, background: 'var(--color-bg-lilac-strong)', color: 'var(--color-primary)', fontSize: 12, fontWeight: 600 }}>
                        {r}
                        <button onClick={() => removeRole(u, r)} aria-label={t('settings.users.removeRole')} title={t('settings.users.removeRole')} style={{ display: 'inline-flex', background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', padding: 0 }}><Icon name="x" size={13} /></button>
                      </span>
                    ))}
                    {addingFor === u.id ? (
                      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {allRoles.filter((rn) => !(u.roles || []).includes(rn)).map((rn) => (
                          <button key={rn} onClick={() => addRole(u, rn)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 999, border: '1.5px solid var(--color-primary)', background: '#fff', color: 'var(--color-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}><Icon name="plus" size={12} /> {rn}</button>
                        ))}
                        <button onClick={() => setAddingFor(null)} title={t('common.cancel')} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', display: 'inline-flex' }}><Icon name="x" size={14} /></button>
                      </span>
                    ) : allRoles.filter((rn) => !(u.roles || []).includes(rn)).length > 0 && (
                      <button onClick={() => setAddingFor(u.id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, border: '1.5px dashed var(--color-border-strong)', background: '#fff', color: 'var(--color-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}><Icon name="plus" size={12} /> {t('settings.users.addRole')}</button>
                    )}
                  </div>
                </td>
                <td style={utd}><div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {!u.is_owner && !self && <IconButton icon="trash" variant="danger" title={t('settings.users.remove')} onClick={() => remove(u)} />}
                </div></td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {assigning && <AssignModal t={t} companyId={currentCompanyId} onClose={() => setAssigning(false)} onAssigned={(created) => { setAssigning(false); toast(t(created ? 'settings.users.invited' : 'settings.users.assigned'), 'success'); load() }} notify={notify} />}
    </Card>
  )
}

function AssignModal({ t, companyId, onClose, onAssigned, notify }) {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [needDetails, setNeedDetails] = useState(false) // email has no account yet
  const [saving, setSaving] = useState(false)
  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const { data } = await api.post(`/companies/${companyId}/users/assign`, { email, first_name: firstName.trim() || undefined, last_name: lastName.trim() || undefined, roles: [] })
      onAssigned(!!data?.created)
    } catch (e2) {
      // Email has no Invoo account yet → reveal the name fields to invite them.
      if (e2?.response?.data?.code === 'USER_DETAILS_REQUIRED') { setNeedDetails(true); setSaving(false); return }
      notify({ tone: 'error', title: t('settings.error'), message: e2?.response?.data?.error || t('settings.users.assignError') })
    } finally { setSaving(false) }
  }
  return (
    <Modal open onClose={onClose} maxWidth={460} closeOnBackdrop={false}>
      <form onSubmit={submit} style={{ padding: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 6 }}>{t('settings.users.assign')}</h3>
        <p style={{ color: 'var(--color-text)', fontSize: 13.5, marginBottom: 14 }}>{t('settings.users.assignHint')}</p>
        <Field label={t('settings.users.email')} required><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus /></Field>
        {needDetails && (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', background: 'var(--color-bg-lilac)', borderRadius: 10, fontSize: 12.5, color: 'var(--color-text)', marginBottom: 14 }}>
              <span style={{ color: 'var(--color-primary)', flexShrink: 0 }}><Icon name="info" size={15} /></span>{t('settings.users.inviteHint')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label={t('settings.users.firstName')} required><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required /></Field>
              <Field label={t('settings.users.lastName')} required><Input value={lastName} onChange={(e) => setLastName(e.target.value)} required /></Field>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
          <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" icon={needDetails ? 'send' : 'check'} loading={saving}>{needDetails ? t('settings.users.invite') : t('settings.users.assign')}</Button>
        </div>
      </form>
    </Modal>
  )
}

const uth = { textAlign: 'left', padding: '12px 20px', fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }
const utd = { padding: '13px 20px', color: 'var(--color-ink)', fontSize: 14 }
